import React, { useEffect, useState } from "react";
import styles from './NotificationPanel.module.scss';
import Toggle from "../toggle/Toggle";
import ProfilePicture from "../profilePicture/ProfilePicture";
import axiosClient from "../../api/axiosClient";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import PropTypes from "prop-types";
import {ReactComponent as XIcon} from '../header/X.svg';
import { useNavigate } from "react-router-dom";

const NOTIFICATION_ROUTES = {
  EVALUATION_REQUEST_CREATED: () => '/evaluations',
  EVALUATION_REQUEST_TRANSFERRED: () => '/evaluations',
  EVALUATION_REQUEST_ACCEPTED: () => '/evaluations',
  EVALUATION_REQUEST_REJECTED: () => '/evaluations',
  EVALUATION_COMPLETED: (referenceId) => `/student-overview/evaluation/${referenceId}/summary`,
  PENDING_EVALUATION_REMINDER: (referenceId) => `/student-overview/evaluation/${referenceId}`,
  CASE_LOG_INCOMPLETE_REMINDER: (referenceId) => `/log-reports/${referenceId}/case-logs`,
  PROGRAM_UPDATED: (referenceId) => `/programs/edit/${referenceId}`,
  SURVEY_REQUESTED_CLINICAL_SITE: `/surveys`,
  TEMPORARY_INSTRUCTOR_ADDED: `/instructors`,
  EVALUATION_SUBMITTED_FOR_REVIEW: (referenceId) => `/evaluations/${referenceId}/summary`
};

const NotificationPanel = ({ onClose, initialNotifications, onNotificationsChange, onMarkAllRead }) => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [notifications, setNotifications] = useState(initialNotifications);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  useEffect(() => {
    if (!unreadOnly) {
      setNotifications(initialNotifications);
      return;
    }
    setIsLoading(true);
    axiosClient.get('notifications', { params: { isRead: false } })
      .then((response) => {
        if (response.data?.notifications) {
          setNotifications(response.data.notifications);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [unreadOnly, initialNotifications]);

  const markAsRead = (id) => {
    axiosClient.patch(`notifications/${id}/read`)
      .then(() => {
        setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
        onNotificationsChange((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
      })
      .catch(() => {});
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    const routeFn = NOTIFICATION_ROUTES[notification.type];
    if (routeFn) {
      navigate(routeFn(notification.referenceId));
    }
    onClose();
  };

  const handleMarkAllRead = () => {
    axiosClient.patch('notifications/read-all')
      .then(() => {
        const updated = notifications.map((n) => ({ ...n, isRead: true }));
        setNotifications(updated);
        onNotificationsChange((prev) => prev.map((n) => ({ ...n, isRead: true })));
        onMarkAllRead();
      })
      .catch(() => {});
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.title}>Notifications</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <XIcon />
          </button>
        </div>

        <div className={styles.toolbar}>
          <Toggle
            checked={unreadOnly}
            onChange={() => setUnreadOnly((prev) => !prev)}
            label="Unread only"
            labelPosition="right"
          />
          {unreadCount > 0 && (
            <button className={styles.markAllRead} onClick={handleMarkAllRead}>
              Mark all as read
            </button>
          )}
        </div>

        <div className={styles.list}>
          {isLoading ? (
            <div className={styles.spinner}>
              <LoadingSpinner size={32} />
            </div>
          ) : notifications.length === 0 ? (
            <div className={styles.empty}>
              <p>No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`${styles.item} ${!notification.isRead ? styles.unread : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className={styles.avatar}>
                  {notification.actor ? (
                    <ProfilePicture
                      profilePicture={notification.actor.profilePicture}
                      firstName={notification.actor.firstName}
                      lastName={notification.actor.lastName}
                      size="small"
                    />
                  ) : (
                    <div className={styles.systemIcon}>
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <circle cx="20" cy="20" r="20" fill="#F08D02" />
                        <path d="M20 14v8M20 26h.01" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className={styles.content}>
                  <p className={styles.message}>{notification.description}</p>
                  <span className={styles.time}>
                    {new Date(notification.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                {!notification.isRead && <span className={styles.dot} />}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

NotificationPanel.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialNotifications: PropTypes.array.isRequired,
  onNotificationsChange: PropTypes.func.isRequired,
  onMarkAllRead: PropTypes.func.isRequired,
};

export default NotificationPanel;
